import { FetchClient } from '@foundatiofx/fetchclient';

/** 'Stalled' is synthesized server-side for an op that stopped reporting activity — its worker died or
 *  dropped the message. It's shown as finished (revivable, not a real failure). */
export type OperationStatus = 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Cancelled' | 'Stalled';
export type ProgressMode = 'Counter' | 'Steps' | 'Determinate';

export interface TrackedOperation {
    trackingId: string;
    type: string;
    label: string;
    status: OperationStatus;
    mode: ProgressMode;
    total: number;
    processed: number;
    failed: number;
    skipped: number;
    message?: string | null;
    error?: string | null;
    cancellable: boolean;
    /** Whether the op left a durable Job History activity (false for a suppressed successful no-op). */
    wroteJobRun: boolean;
    isCancellationRequested: boolean;
    linkUrl?: string | null;
    linkLabel?: string | null;
    tags?: Record<string, string> | null;
    metadata?: Record<string, string> | null;
    createdUtc: string;
    startedUtc?: string | null;
    finishedUtc?: string | null;
}

export function isActiveOperation(op: TrackedOperation): boolean {
    return op.status === 'Pending' || op.status === 'Running';
}

export class OperationsApi {
    constructor(
        private fetch = new FetchClient(),
        private route = 'operations'
    ) {}

    async list() {
        return await this.fetch.getJSON<TrackedOperation[]>(this.route);
    }

    /** Every client's operations, queued and running. Global admins only (gated server-side). */
    async listGlobal() {
        return await this.fetch.getJSON<TrackedOperation[]>(`${this.route}/global`);
    }

    async listForClient(clientId: number, productId?: number) {
        return await this.fetch.getJSON<TrackedOperation[]>(`${this.route}/client/${clientId}`, {
            params: productId ? { productId } : undefined
        });
    }

    async get(id: string) {
        return await this.fetch.getJSON<TrackedOperation>(`${this.route}/${id}`);
    }

    async cancel(id: string) {
        return await this.fetch.postJSON(`${this.route}/${id}/cancel`, {});
    }

    async dismiss(id: string) {
        return await this.fetch.postJSON(`${this.route}/${id}/dismiss`, {});
    }

    async clearFinished() {
        return await this.fetch.postJSON(`${this.route}/clear-finished`, {});
    }
}
